import React, {
  createContext, useContext, useState, useEffect,
  useCallback, useRef,
} from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { NotificacionMia } from 'types/notificacion.types';
import { notificacionService } from 'services/notificacionService';
import { tokenStorage } from 'utils/tokenStorage';
import { httpClient } from 'utils/httpClient';
import { useAuth } from 'contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────
// NotificacionContext
// – Carga GET /mias al iniciar sesión
// – Mantiene conexión SSE para notificaciones en tiempo real
// – Expone funciones de marcar leída / confirmar / marcar todas
// ─────────────────────────────────────────────────────────────────

interface NotificacionContextValue {
  notificaciones:    NotificacionMia[];
  noLeidas:          number;
  sinConfirmar:      number;    // comunicados con requiereConfirmacion && !confirmada
  loading:           boolean;
  toast:             NotificacionMia | null;
  descartarToast:    () => void;
  marcarLeida:       (id: number) => Promise<void>;
  marcarTodasLeidas: () => Promise<void>;
  confirmar:         (id: number) => Promise<void>;
  recargar:          () => Promise<void>;
}

const NotificacionContext = createContext<NotificacionContextValue | null>(null);

export const useNotificaciones = (): NotificacionContextValue => {
  const ctx = useContext(NotificacionContext);
  if (!ctx) throw new Error('useNotificaciones debe usarse dentro de NotificacionProvider');
  return ctx;
};

export const NotificacionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState<NotificacionMia[]>([]);
  const [loading, setLoading]               = useState(false);
  const [toast, setToast]                   = useState<NotificacionMia | null>(null);
  const abortRef    = useRef<AbortController | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const noLeidas    = notificaciones.filter(n => !n.leida).length;
  const sinConfirmar = notificaciones.filter(
    n => n.requiereConfirmacion && !n.confirmada
  ).length;

  // ── Cargar notificaciones REST ────────────────────────────────
  const recargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificacionService.getMias();
      setNotificaciones(data);
    } catch {} finally { setLoading(false); }
  }, []);

  // ── Mostrar toast con auto-dismiss ────────────────────────────
  const mostrarToast = useCallback((notif: NotificacionMia) => {
    setToast(notif);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    // Comunicados con confirmación requerida duran más (10 s)
    const delay = notif.requiereConfirmacion ? 10_000 : 5_000;
    toastTimerRef.current = setTimeout(() => setToast(null), delay);
  }, []);

  const descartarToast = useCallback(() => {
    setToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // ── Conexión SSE ──────────────────────────────────────────────
  const conectarSSE = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const apiUrl = process.env.REACT_APP_API_URL ?? '';
    const token  = tokenStorage.getAccessToken();
    if (!token) return;

    fetchEventSource(`${apiUrl}/notificaciones/suscribir`, {
      headers: {
        Authorization:   `Bearer ${token}`,
        'X-Client-Type': 'WEB',
      },
      signal: controller.signal,
      openWhenHidden: true,
      onmessage(event) {
        if (event.event === 'nueva-notificacion') {
          try {
            const payload = JSON.parse(event.data);
            // El backend reutiliza el canal "nueva-notificacion" para emitir
            // un kick de sesión cuando otro dispositivo desplaza al usuario.
            // Lo detectamos por el campo `tipo` y disparamos logout inmediato.
            if (payload?.tipo === 'session-replaced') {
              controller.abort();
              httpClient.forceLogout('session-replaced');
              return;
            }
            const notif: NotificacionMia = payload;
            setNotificaciones(prev => [notif, ...prev]);
            mostrarToast(notif);
          } catch {}
        } else if (event.event === 'session-replaced') {
          // Por si el backend lo emite como tipo de evento independiente.
          controller.abort();
          httpClient.forceLogout('session-replaced');
        }
      },
      onerror(err) {
        throw err;
      },
    }).catch(() => {});
  }, [mostrarToast]);

  // ── Ciclo de vida ─────────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      setNotificaciones([]);
      setToast(null);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    recargar();
    conectarSSE();

    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.idUsuario]);

  // ── Acciones ──────────────────────────────────────────────────
  const marcarLeida = useCallback(async (id: number) => {
    try {
      const updated = await notificacionService.marcarLeida(id);
      setNotificaciones(prev =>
        prev.map(n => n.idNotificacion === id ? updated : n)
      );
    } catch {}
  }, []);

  const marcarTodasLeidas = useCallback(async () => {
    const pendientes = notificaciones.filter(n => !n.leida);
    setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    try {
      await Promise.all(pendientes.map(n => notificacionService.marcarLeida(n.idNotificacion)));
    } catch {
      await recargar();
    }
  }, [notificaciones, recargar]);

  const confirmar = useCallback(async (id: number) => {
    // Actualización optimista
    const ahora = new Date().toISOString();
    setNotificaciones(prev =>
      prev.map(n =>
        n.idNotificacion === id
          ? { ...n, confirmada: true, fechaConfirmacion: ahora }
          : n
      )
    );
    try {
      const updated = await notificacionService.confirmar(id);
      setNotificaciones(prev =>
        prev.map(n => n.idNotificacion === id ? updated : n)
      );
    } catch {
      // Revertir si falla
      setNotificaciones(prev =>
        prev.map(n =>
          n.idNotificacion === id
            ? { ...n, confirmada: false, fechaConfirmacion: null }
            : n
        )
      );
    }
  }, []);

  return (
    <NotificacionContext.Provider value={{
      notificaciones, noLeidas, sinConfirmar, loading,
      toast, descartarToast,
      marcarLeida, marcarTodasLeidas, confirmar, recargar,
    }}>
      {children}
    </NotificacionContext.Provider>
  );
};
